import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import * as api from "../lib/supabase";
import { supabase } from "../lib/supabase";
import VoiceButton from "../components/voice/VoiceButton";

// Edit indicator component with hover tooltip
function EditIndicator({ entry, editHistoryMap, onLoadHistory }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const indicatorRef = useRef(null);

  if (!entry.edited_at) return null;

  const history = editHistoryMap[entry.id] || [];
  const isLoading = history === "loading";

  const handleMouseEnter = async (e) => {
    // Position tooltip
    if (indicatorRef.current) {
      const rect = indicatorRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left - 100,
      });
    }
    setShowTooltip(true);

    // Load history if not already loaded
    if (!editHistoryMap[entry.id]) {
      onLoadHistory(entry.id);
    }
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return "-";
    const d = new Date(isoStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatTimeOnly = (isoStr) => {
    if (!isoStr) return "-";
    const d = new Date(isoStr);
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <span
      ref={indicatorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 4,
        padding: "2px 6px",
        background: "#fef3c7",
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 600,
        color: "#92400e",
        cursor: "help",
        position: "relative",
      }}
    >
      edited
      {showTooltip && (
        <div
          style={{
            position: "fixed",
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: 9999,
            background: "#1e293b",
            color: "#f8fafc",
            borderRadius: 8,
            padding: 12,
            minWidth: 220,
            maxWidth: 300,
            fontSize: 12,
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontWeight: 600, marginBottom: 8, color: "#fbbf24" }}>
            Edit History
          </div>
          {isLoading ? (
            <div style={{ color: "#94a3b8" }}>Loading...</div>
          ) : history.length === 0 ? (
            <div style={{ color: "#94a3b8" }}>No edit history found</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {history.slice(0, 5).map((edit, idx) => (
                <div
                  key={edit.id || idx}
                  style={{
                    borderBottom:
                      idx < history.length - 1 && idx < 4
                        ? "1px solid #334155"
                        : "none",
                    paddingBottom: idx < history.length - 1 && idx < 4 ? 8 : 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 2,
                    }}
                  >
                    <span style={{ color: "#94a3b8", fontSize: 10 }}>
                      {edit.field_name === "clock_in"
                        ? "Clock In"
                        : "Clock Out"}
                    </span>
                    <span style={{ color: "#64748b", fontSize: 10 }}>
                      {formatDateTime(edit.edited_at)}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        color: "#ef4444",
                        textDecoration: "line-through",
                      }}
                    >
                      {formatTimeOnly(edit.old_value)}
                    </span>
                    <span style={{ color: "#64748b" }}>→</span>
                    <span style={{ color: "#10b981" }}>
                      {formatTimeOnly(edit.new_value)}
                    </span>
                  </div>
                  {edit.editor && (
                    <div
                      style={{ color: "#64748b", fontSize: 10, marginTop: 2 }}
                    >
                      by{" "}
                      {edit.editor.display_name ||
                        `${edit.editor.first_name} ${edit.editor.last_name}`}
                    </div>
                  )}
                </div>
              ))}
              {history.length > 5 && (
                <div
                  style={{
                    color: "#64748b",
                    fontSize: 10,
                    fontStyle: "italic",
                  }}
                >
                  + {history.length - 5} more edits
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

const colors = {
  primary: "#1a5a8e",
  white: "#ffffff",
  gray50: "#f8fafc",
  gray100: "#f1f5f9",
  gray200: "#e2e8f0",
  gray300: "#cbd5e1",
  gray400: "#94a3b8",
  gray500: "#64748b",
  gray600: "#475569",
  gray700: "#334155",
  gray800: "#1e293b",
  success: "#10b981",
  successLight: "#d1fae5",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
};

export default function MyTimesheets({ currentUser, orgId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [timeEntriesMap, setTimeEntriesMap] = useState({});
  const [loadingEntries, setLoadingEntries] = useState({});
  const [costCodesMap, setCostCodesMap] = useState({});
  const [allocationsMap, setAllocationsMap] = useState({});
  const [savingAllocations, setSavingAllocations] = useState(false);
  const [approvingReport, setApprovingReport] = useState(null);

  // Multi-select and quick allocation state
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [selectedCostCode, setSelectedCostCode] = useState(null);
  const [quickHours, setQuickHours] = useState("");

  // Bulk mode state
  const [bulkMode, setBulkMode] = useState(false);

  // Time edit modal state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editClockIn, setEditClockIn] = useState("");
  const [editClockOut, setEditClockOut] = useState("");

  // Cost code picker state
  const [pickingCostCode, setPickingCostCode] = useState(null); // entry being edited

  // Add crew modal state
  const [showAddCrew, setShowAddCrew] = useState(null); // reportId when open
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [addingEmployee, setAddingEmployee] = useState(null);

  // Edit history for audit trail
  const [editHistoryMap, setEditHistoryMap] = useState({});

  const loadEditHistory = async (entryId) => {
    // Mark as loading
    setEditHistoryMap((prev) => ({ ...prev, [entryId]: "loading" }));
    try {
      const history = await api.getTimeEntryEditHistory(entryId);
      setEditHistoryMap((prev) => ({ ...prev, [entryId]: history || [] }));
    } catch (error) {
      console.error("Error loading edit history:", error);
      setEditHistoryMap((prev) => ({ ...prev, [entryId]: [] }));
    }
  };

  // Load all employees for add crew modal
  const loadAllEmployees = async () => {
    setLoadingEmployees(true);
    try {
      // Get employees directly in this org
      const { data: directEmployees, error: error1 } = await supabase
        .from("t4s_employees")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("last_name");

      // Get employees linked via t4s_employee_orgs
      const { data: linkedEmployees, error: error2 } = await supabase
        .from("t4s_employees")
        .select("*, t4s_employee_orgs!inner(org_id)")
        .eq("t4s_employee_orgs.org_id", orgId)
        .eq("is_active", true)
        .order("last_name");

      if (error1) console.error("Error loading direct employees:", error1);
      if (error2) console.error("Error loading linked employees:", error2);

      // Combine and deduplicate
      const allEmps = [...(directEmployees || []), ...(linkedEmployees || [])];
      const unique = allEmps.filter(
        (emp, idx, arr) => arr.findIndex((e) => e.id === emp.id) === idx,
      );
      setAllEmployees(unique);
    } catch (err) {
      console.error("Error loading employees:", err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Add crew member to a report (create time entry)
  const handleAddCrewMember = async (employee, report) => {
    setAddingEmployee(employee.id);
    try {
      // Create a time entry for this employee on this job/date
      const timeEntry = await api.clockIn({
        orgId,
        employeeId: employee.id,
        jobId: report.job_id,
        jobTimezone: report.job?.timezone || "America/Denver",
      });

      // Also create a job assignment if needed
      const { error: assignError } = await supabase
        .from("t4s_job_assignments")
        .insert({
          org_id: orgId,
          job_id: report.job_id,
          employee_id: employee.id,
          assigned_date: report.report_date,
        });

      if (assignError && assignError.code !== "23505") {
        console.error("Error creating assignment:", assignError);
      }

      // Reload time entries for this report
      setTimeEntriesMap((prev) => ({ ...prev, [report.id]: null }));
      await loadTimeEntries(report);

      setShowAddCrew(null);
    } catch (error) {
      console.error("Error adding crew member:", error);
      toast.error("Failed to add crew member: " + error.message);
    } finally {
      setAddingEmployee(null);
    }
  };

  // Get available employees (not already in this report's entries)
  const getAvailableEmployees = (report) => {
    const entries = timeEntriesMap[report.id] || [];
    const existingIds = new Set(entries.map((e) => e.employee_id));
    return allEmployees.filter((emp) => !existingIds.has(emp.id));
  };

  // Voice state
  const [voiceProcessing, setVoiceProcessing] = useState(null); // reportId being processed
  const [voiceError, setVoiceError] = useState(null);

  // Helper to convert HH:MM to ISO timestamp for a given date
  const parseTimeToISO = (dateStr, timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(":").map(Number);
    const d = new Date(dateStr + "T12:00:00");
    d.setHours(hours, minutes, 0, 0);
    return d.toISOString();
  };

  // Handle universal voice result (time edits + allocations)
  const handleVoiceResult = async (result, report) => {
    const intent = result?.detected_intent;
    const entries = timeEntriesMap[report.id] || [];
    const jobCostCodes = costCodesMap[report.job_id] || [];

    setSavingAllocations(true);
    setVoiceError(null);

    try {
      // Handle time edits
      if (intent === "time_edit" || intent === "time_and_allocation") {
        const edits = result?.time_edit?.edits || [];
        for (const edit of edits) {
          const entry = findEntryByName(entries, edit.employee_name);
          if (!entry) {
            console.warn(
              "No matching entry for time edit:",
              edit.employee_name,
            );
            continue;
          }

          // Build update params
          const updates = {};
          if (edit.clock_in) {
            updates.clockIn = parseTimeToISO(report.report_date, edit.clock_in);
          }
          if (edit.clock_out) {
            updates.clockOut = parseTimeToISO(
              report.report_date,
              edit.clock_out,
            );
          }

          if (Object.keys(updates).length > 0) {
            await api.updateTimeEntry(entry.id, {
              ...updates,
              editedBy: currentUser?.id,
              editSource: "voice",
            });
          }
        }
      }

      // Handle allocations
      if (intent === "time_allocation" || intent === "time_and_allocation") {
        const allocations = result?.time_allocation?.allocations || [];
        for (const alloc of allocations) {
          const entry = findEntryByName(entries, alloc.employee_name);
          if (!entry) {
            console.warn(
              "No matching entry for allocation:",
              alloc.employee_name,
            );
            continue;
          }

          // Find matching cost code
          let costCode = null;
          if (alloc.cost_code_hint) {
            costCode = jobCostCodes.find(
              (cc) =>
                cc.code
                  ?.toLowerCase()
                  .includes(alloc.cost_code_hint.toLowerCase()) ||
                cc.name
                  ?.toLowerCase()
                  .includes(alloc.cost_code_hint.toLowerCase()),
            );
          }
          if (!costCode && alloc.activity) {
            costCode = jobCostCodes.find(
              (cc) =>
                cc.name?.toLowerCase().includes(alloc.activity.toLowerCase()) ||
                cc.code?.toLowerCase().includes(alloc.activity.toLowerCase()),
            );
          }

          if (!costCode) {
            console.warn(
              "No matching cost code for:",
              alloc.cost_code_hint || alloc.activity,
            );
            continue;
          }

          // Get current allocations for this entry
          const currentAllocs = allocationsMap[entry.id] || {};
          const updatedAllocs = {
            ...currentAllocs,
            [costCode.id]: alloc.hours,
          };

          // Convert to array format for API
          const allocArray = Object.entries(updatedAllocs)
            .filter(([_, h]) => parseFloat(h) > 0)
            .map(([costCodeId, h]) => ({
              costCodeId,
              hours: parseFloat(h),
            }));

          await api.saveAllocations(entry.id, allocArray, currentUser.id);

          setAllocationsMap((prev) => ({
            ...prev,
            [entry.id]: updatedAllocs,
          }));
        }
      }

      // Handle add_crew intent
      if (intent === "add_crew") {
        const employees = result?.add_crew?.employees || [];
        if (employees.length > 0) {
          // Load employees if not already loaded
          if (allEmployees.length === 0) {
            await loadAllEmployees();
          }

          for (const emp of employees) {
            // Find matching employee by name
            const matchedEmp = allEmployees.find((e) => {
              const fullName =
                `${e.first_name || ""} ${e.last_name || ""}`.toLowerCase();
              const displayName = (e.display_name || "").toLowerCase();
              const searchName = emp.employee_name.toLowerCase();
              return (
                fullName.includes(searchName) ||
                displayName.includes(searchName) ||
                searchName.includes(e.first_name?.toLowerCase() || "")
              );
            });

            if (matchedEmp) {
              // Check if already in entries
              const existingIds = new Set(entries.map((e) => e.employee_id));
              if (!existingIds.has(matchedEmp.id)) {
                await handleAddCrewMember(matchedEmp, report);
              }
            } else {
              console.warn("Could not find employee:", emp.employee_name);
            }
          }
        }
      }

      // Handle simple note or unrecognized intent
      if (!intent || intent === "note") {
        setVoiceError(
          "Could not understand command. Try: 'John 7am to 3pm, 8 hours block' or 'Add Mike to crew'",
        );
      }

      // Reload entries to refresh state
      setTimeEntriesMap((prev) => ({ ...prev, [report.id]: null }));
      await loadTimeEntries(report);
    } catch (error) {
      console.error("Error applying voice command:", error);
      setVoiceError("Failed to apply: " + error.message);
    } finally {
      setSavingAllocations(false);
      setVoiceProcessing(null);
    }
  };

  // Helper to find entry by employee name (fuzzy match)
  const findEntryByName = (entries, name) => {
    if (!name) return null;
    const nameLower = name.toLowerCase().trim();

    // Try exact match first
    let match = entries.find((e) => {
      const displayName = (e.employee?.display_name || "").toLowerCase();
      const fullName =
        `${e.employee?.first_name || ""} ${e.employee?.last_name || ""}`
          .toLowerCase()
          .trim();
      const reverseName =
        `${e.employee?.last_name || ""}, ${e.employee?.first_name || ""}`
          .toLowerCase()
          .trim();
      return (
        displayName === nameLower ||
        fullName === nameLower ||
        reverseName === nameLower
      );
    });

    if (match) return match;

    // Try first name match
    match = entries.find((e) => {
      const firstName = (e.employee?.first_name || "").toLowerCase();
      return (
        firstName === nameLower ||
        firstName.startsWith(nameLower) ||
        nameLower.startsWith(firstName)
      );
    });

    if (match) return match;

    // Try partial/fuzzy match
    match = entries.find((e) => {
      const displayName = (e.employee?.display_name || "").toLowerCase();
      const fullName =
        `${e.employee?.first_name || ""} ${e.employee?.last_name || ""}`.toLowerCase();
      return (
        displayName.includes(nameLower) ||
        fullName.includes(nameLower) ||
        nameLower.includes(e.employee?.first_name?.toLowerCase() || "")
      );
    });

    return match;
  };

  useEffect(() => {
    if (orgId && currentUser?.id) {
      loadReports();
    }
  }, [orgId, currentUser?.id]);

  const loadReports = async () => {
    setLoading(true);
    try {
      // Always show only reports where current user is the foreman
      // Admin view is in operations-desk, not the foreman mobile app
      const data = await api.getForemanUnapprovedReports(orgId, currentUser.id);
      setReports(data || []);

      // Load time entries for all reports to show allocation status
      for (const report of data || []) {
        loadTimeEntriesBackground(report);
      }
    } catch (error) {
      console.error("Error loading reports:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load entries in background without blocking UI
  const loadTimeEntriesBackground = async (report) => {
    if (timeEntriesMap[report.id]) return;

    try {
      const entries = await api.getTimeEntries(
        orgId,
        report.report_date,
        report.job_id,
      );
      setTimeEntriesMap((prev) => ({ ...prev, [report.id]: entries || [] }));

      // Initialize allocations from existing data
      const newAllocations = {};
      (entries || []).forEach((entry) => {
        if (entry.allocations?.length > 0) {
          newAllocations[entry.id] = {};
          entry.allocations.forEach((alloc) => {
            newAllocations[entry.id][alloc.cost_code_id] = alloc.hours;
          });
        }
      });
      setAllocationsMap((prev) => ({ ...prev, ...newAllocations }));
    } catch (error) {
      console.error("Error loading time entries for report:", report.id, error);
    }
  };

  const loadTimeEntries = async (report) => {
    if (timeEntriesMap[report.id]) return;

    setLoadingEntries((prev) => ({ ...prev, [report.id]: true }));
    try {
      const entries = await api.getTimeEntries(
        orgId,
        report.report_date,
        report.job_id,
      );
      setTimeEntriesMap((prev) => ({ ...prev, [report.id]: entries || [] }));

      // Initialize allocations from existing data
      const newAllocations = {};
      (entries || []).forEach((entry) => {
        if (entry.allocations?.length > 0) {
          newAllocations[entry.id] = {};
          entry.allocations.forEach((alloc) => {
            newAllocations[entry.id][alloc.cost_code_id] = alloc.hours;
          });
        }
      });
      setAllocationsMap((prev) => ({ ...prev, ...newAllocations }));

      // Load cost codes for this job (same approach as TimeAllocation)
      if (!costCodesMap[report.job_id]) {
        try {
          // First try direct job cost codes
          const { data: jobCodes, error: jobError } = await supabase
            .from("t4s_job_cost_codes")
            .select("*, cost_code:t4s_cost_codes(*)")
            .eq("job_id", report.job_id)
            .eq("is_active", true)
            .order("sort_order");

          if (jobError) {
            console.error("Error loading job cost codes:", jobError);
          }

          let costCodes = [];
          if (jobCodes && jobCodes.length > 0) {
            // Use direct job cost codes
            costCodes = jobCodes
              .map((jcc) => ({
                id: jcc.cost_code?.id,
                code: jcc.cost_code?.code,
                name: jcc.cost_code?.name,
                unit: jcc.cost_code?.unit,
              }))
              .filter((cc) => cc.id);
          } else {
            // Fall back to template-based cost codes
            const { data: job } = await supabase
              .from("t4s_jobs")
              .select("cost_code_template, org_id")
              .eq("id", report.job_id)
              .single();

            if (job?.cost_code_template) {
              const { data: template } = await supabase
                .from("t4s_cost_code_templates")
                .select("id")
                .eq("org_id", job.org_id)
                .eq("slug", job.cost_code_template)
                .single();

              if (template) {
                const { data: templateItems } = await supabase
                  .from("t4s_cost_code_template_items")
                  .select("sort_order, cost_code:t4s_cost_codes(*)")
                  .eq("template_id", template.id)
                  .order("sort_order");

                if (templateItems && templateItems.length > 0) {
                  costCodes = templateItems
                    .map((item) => ({
                      id: item.cost_code?.id,
                      code: item.cost_code?.code,
                      name: item.cost_code?.name,
                      unit: item.cost_code?.unit,
                    }))
                    .filter((cc) => cc.id);
                }
              }
            }

            // Final fallback to org-level cost codes
            if (costCodes.length === 0) {
              const orgCodes = await api.getCostCodes(orgId);
              costCodes = orgCodes || [];
            }
          }

          setCostCodesMap((prev) => ({
            ...prev,
            [report.job_id]: costCodes,
          }));
        } catch (err) {
          console.error("Error loading cost codes:", err);
        }
      }
    } catch (error) {
      console.error("Error loading time entries:", error);
    } finally {
      setLoadingEntries((prev) => ({ ...prev, [report.id]: false }));
    }
  };

  const toggleExpand = (report) => {
    if (expandedReportId === report.id) {
      setExpandedReportId(null);
      setSelectedEntries(new Set());
      setSelectedCostCode(null);
      setQuickHours("");
    } else {
      setExpandedReportId(report.id);
      setSelectedEntries(new Set());
      setSelectedCostCode(null);
      setQuickHours("");
      loadTimeEntries(report);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    return new Date(timeStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "draft":
        return { bg: colors.gray200, color: colors.gray600, label: "Draft" };
      case "submitted":
        return {
          bg: colors.success + "20",
          color: colors.success,
          label: "Sent to Office",
        };
      case "approved":
        return {
          bg: colors.success,
          color: colors.white,
          label: "Locked",
        };
      case "needs_correction":
        return {
          bg: colors.warning + "20",
          color: colors.warning,
          label: "Needs Fix",
        };
      default:
        return { bg: colors.gray200, color: colors.gray600, label: status };
    }
  };

  const getReportTotalHours = (reportId) => {
    const entries = timeEntriesMap[reportId] || [];
    return entries
      .reduce((sum, entry) => sum + (entry.total_hours || 0), 0)
      .toFixed(1);
  };

  const getAllocatedHours = (entryId) => {
    const allocs = allocationsMap[entryId] || {};
    return Object.values(allocs).reduce(
      (sum, h) => sum + (parseFloat(h) || 0),
      0,
    );
  };

  // Get allocation status for a report (for displaying on collapsed cards)
  const getReportAllocationStatus = (reportId) => {
    const entries = timeEntriesMap[reportId] || [];
    if (entries.length === 0) return null; // Not loaded yet

    const totalHours = entries.reduce(
      (sum, e) => sum + (e.total_hours || 0),
      0,
    );
    const allocatedHours = entries.reduce(
      (sum, e) => sum + getAllocatedHours(e.id),
      0,
    );

    const isFullyAllocated = api.isEntryFullyAllocated(
      totalHours,
      allocatedHours,
    );
    const percentage =
      totalHours > 0 ? Math.round((allocatedHours / totalHours) * 100) : 0;

    return { totalHours, allocatedHours, isFullyAllocated, percentage };
  };

  // Toggle entry selection
  const toggleEntrySelection = (entryId) => {
    setSelectedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  // Select all entries
  const selectAllEntries = (entries) => {
    if (selectedEntries.size === entries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(entries.map((e) => e.id)));
    }
  };

  // Apply allocation to selected entries
  const applyAllocation = async (report) => {
    if (selectedEntries.size === 0 || !selectedCostCode || !quickHours) return;

    const hours = parseFloat(quickHours);
    const entries = timeEntriesMap[report.id] || [];

    // Check for over-allocation before applying
    const overAllocated = [];
    for (const entryId of selectedEntries) {
      const entry = entries.find((e) => e.id === entryId);
      if (!entry) continue;

      const totalHours = entry.total_hours || 0;
      const currentAllocs = allocationsMap[entryId] || {};

      // Calculate what total would be with new allocation
      // (replacing existing allocation for this cost code, not adding)
      const otherAllocsTotal = Object.entries(currentAllocs)
        .filter(([ccId]) => ccId !== selectedCostCode.id)
        .reduce((sum, [_, h]) => sum + (parseFloat(h) || 0), 0);

      const newTotal = otherAllocsTotal + hours;

      if (newTotal > totalHours + 0.01) {
        // Small tolerance for rounding
        const empName =
          entry.employee?.display_name ||
          `${entry.employee?.first_name || ""} ${entry.employee?.last_name || ""}`.trim() ||
          "Unknown";
        overAllocated.push({
          name: empName,
          totalHours,
          wouldBe: newTotal,
        });
      }
    }

    if (overAllocated.length > 0) {
      const names = overAllocated
        .map(
          (e) =>
            `${e.name} (${e.totalHours}h total, would be ${e.wouldBe.toFixed(1)}h)`,
        )
        .join("\n");
      toast.error(
        `Cannot allocate ${hours}h - would exceed total hours for some crew members`,
      );
      return;
    }

    setSavingAllocations(true);
    try {
      for (const entryId of selectedEntries) {
        // Get current allocations for this entry
        const currentAllocs = allocationsMap[entryId] || {};

        // Add/update the allocation for selected cost code
        const updatedAllocs = {
          ...currentAllocs,
          [selectedCostCode.id]: hours,
        };

        // Convert to array format for API
        const allocArray = Object.entries(updatedAllocs)
          .filter(([_, h]) => parseFloat(h) > 0)
          .map(([costCodeId, h]) => ({
            costCodeId,
            hours: parseFloat(h),
          }));

        await api.saveAllocations(entryId, allocArray, currentUser.id);

        // Update local state
        setAllocationsMap((prev) => ({
          ...prev,
          [entryId]: updatedAllocs,
        }));
      }

      // Clear selection after applying
      setSelectedEntries(new Set());
      setQuickHours("");

      // Reload entries
      setTimeEntriesMap((prev) => ({ ...prev, [report.id]: null }));
      await loadTimeEntries(report);
    } catch (error) {
      console.error("Error applying allocation:", error);
      toast.error("Failed to apply allocation: " + error.message);
    } finally {
      setSavingAllocations(false);
    }
  };

  const handleApproveReport = async (report) => {
    const entries = timeEntriesMap[report.id] || [];
    const unallocated = entries.filter((e) => {
      const allocated = getAllocatedHours(e.id);
      return (
        e.total_hours && !api.isEntryFullyAllocated(e.total_hours, allocated)
      );
    });

    if (unallocated.length > 0) {
      toast.warning(
        `Please allocate all time before approving. ${unallocated.length} entries still need allocation.`,
      );
      return;
    }

    setApprovingReport(report.id);
    try {
      await api.updateDailyReportStatus(report.id, "approved", currentUser.id);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
      setExpandedReportId(null);
    } catch (error) {
      console.error("Error approving report:", error);
      toast.error("Failed to approve: " + error.message);
    } finally {
      setApprovingReport(null);
    }
  };

  const handleSubmitReport = async (report) => {
    setApprovingReport(report.id);
    try {
      await api.updateDailyReportStatus(report.id, "submitted");
      setReports((prev) =>
        prev.map((r) =>
          r.id === report.id ? { ...r, status: "submitted" } : r,
        ),
      );
    } catch (error) {
      console.error("Error submitting report:", error);
      toast.error("Failed to submit: " + error.message);
    } finally {
      setApprovingReport(null);
    }
  };

  // Open time edit modal
  const openTimeEdit = (entry) => {
    const formatTimeForInput = (isoStr) => {
      if (!isoStr) return "";
      const d = new Date(isoStr);
      const hours = d.getHours().toString().padStart(2, "0");
      const minutes = d.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };
    setEditingEntry(entry);
    setEditClockIn(formatTimeForInput(entry.clock_in));
    setEditClockOut(formatTimeForInput(entry.clock_out));
  };

  // Save time edit
  const saveTimeEdit = async (report) => {
    if (!editingEntry) return;
    try {
      await api.updateTimeEntry(editingEntry.id, {
        clockIn: editClockIn
          ? parseTimeToISO(report.report_date, editClockIn)
          : undefined,
        clockOut: editClockOut
          ? parseTimeToISO(report.report_date, editClockOut)
          : undefined,
        editedBy: currentUser?.id,
        editSource: "manual",
      });
      // Reload entries
      setTimeEntriesMap((prev) => ({ ...prev, [report.id]: null }));
      await loadTimeEntries(report);
      setEditingEntry(null);
    } catch (error) {
      console.error("Error saving time edit:", error);
      toast.error("Failed to save: " + error.message);
    }
  };

  // Assign cost code to single entry
  const assignCostCode = async (entry, costCode, hours, report) => {
    try {
      const currentAllocs = allocationsMap[entry.id] || {};
      const updatedAllocs = { ...currentAllocs, [costCode.id]: hours };

      const allocArray = Object.entries(updatedAllocs)
        .filter(([_, h]) => parseFloat(h) > 0)
        .map(([costCodeId, h]) => ({
          costCodeId,
          hours: parseFloat(h),
        }));

      await api.saveAllocations(entry.id, allocArray, currentUser.id);

      setAllocationsMap((prev) => ({
        ...prev,
        [entry.id]: updatedAllocs,
      }));

      setPickingCostCode(null);

      // Reload to get fresh data
      setTimeEntriesMap((prev) => ({ ...prev, [report.id]: null }));
      await loadTimeEntries(report);
    } catch (error) {
      console.error("Error assigning cost code:", error);
      toast.error("Failed to assign: " + error.message);
    }
  };

  // Get primary allocation for an entry (for display)
  const getPrimaryAllocation = (entry, jobCostCodes) => {
    const allocs = allocationsMap[entry.id] || {};
    const allocEntries = Object.entries(allocs).filter(
      ([_, h]) => parseFloat(h) > 0,
    );
    if (allocEntries.length === 0) return null;

    // Get the first/largest allocation
    const [costCodeId, hours] = allocEntries.sort((a, b) => b[1] - a[1])[0];
    const costCode = jobCostCodes.find((cc) => cc.id === costCodeId);
    return { costCode, hours, count: allocEntries.length };
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: colors.gray100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", color: colors.gray500 }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: colors.gray100 }}>
      {/* Header */}
      <div
        style={{
          padding: "16px",
          background: colors.primary,
          color: colors.white,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: 8,
            padding: "8px 12px",
            color: colors.white,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
            My Timesheets
          </h1>
          <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
            Select crew, pick cost code, apply hours
          </p>
        </div>
        <button
          onClick={loadReports}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: 8,
            padding: "8px 12px",
            color: colors.white,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Refresh
        </button>
      </div>

      <div style={{ padding: 16 }}>
        {reports.length === 0 ? (
          <div
            style={{
              background: colors.white,
              borderRadius: 16,
              padding: 40,
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: 40, marginBottom: 12, color: colors.success }}
            >
              ✓
            </div>
            <h3
              style={{ margin: "0 0 8px", color: colors.gray800, fontSize: 18 }}
            >
              All Caught Up!
            </h3>
            <p style={{ margin: 0, color: colors.gray500, fontSize: 14 }}>
              No timesheets pending review.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {reports.map((report) => {
              const isExpanded = expandedReportId === report.id;
              const statusStyle = getStatusStyle(report.status);
              const entries = timeEntriesMap[report.id] || [];
              const isLoadingEntries = loadingEntries[report.id];
              const jobCostCodes = costCodesMap[report.job_id] || [];
              const allocStatus = getReportAllocationStatus(report.id);
              const isLocked = report.status === "approved"; // Locked by admin - can only edit time

              return (
                <div
                  key={report.id}
                  style={{
                    background: colors.white,
                    borderRadius: 16,
                    overflow: "hidden",
                  }}
                >
                  {/* Report Header */}
                  <div
                    onClick={() => toggleExpand(report)}
                    style={{
                      padding: 16,
                      cursor: "pointer",
                      borderBottom: isExpanded
                        ? `1px solid ${colors.gray200}`
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            color: colors.gray800,
                            fontSize: 15,
                          }}
                        >
                          #{report.job?.job_number} - {report.job?.name}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: colors.gray500,
                            marginTop: 2,
                          }}
                        >
                          {formatDate(report.report_date)}
                          {report.foreman && (
                            <span>
                              {" "}
                              • Foreman:{" "}
                              {report.foreman.display_name ||
                                `${report.foreman.first_name} ${report.foreman.last_name}`}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: statusStyle.bg,
                          color: statusStyle.color,
                        }}
                      >
                        {statusStyle.label}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 12, color: colors.gray400 }}>
                          {isExpanded ? "▼" : "▶"}
                        </span>
                        <span style={{ fontSize: 13, color: colors.gray500 }}>
                          {entries.length > 0
                            ? `${entries.length} crew`
                            : "Tap to expand"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        {/* Allocation Status Indicator */}
                        {allocStatus && (
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 600,
                              background: allocStatus.isFullyAllocated
                                ? colors.successLight
                                : colors.warning + "20",
                              color: allocStatus.isFullyAllocated
                                ? colors.success
                                : colors.warning,
                            }}
                          >
                            {allocStatus.isFullyAllocated
                              ? "Allocated"
                              : `${allocStatus.percentage}%`}
                          </span>
                        )}

                        <div style={{ textAlign: "right" }}>
                          <span
                            style={{
                              fontSize: 20,
                              fontWeight: 700,
                              color: colors.gray800,
                            }}
                          >
                            {report.total_hours?.toFixed(1) ||
                              (entries.length > 0
                                ? getReportTotalHours(report.id)
                                : "-")}
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              color: colors.gray500,
                              marginLeft: 4,
                            }}
                          >
                            hrs
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content - Redesigned */}
                  {isExpanded && (
                    <div style={{ background: colors.white }}>
                      {isLoadingEntries ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: 24,
                            color: colors.gray500,
                          }}
                        >
                          Loading crew...
                        </div>
                      ) : entries.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: 16,
                            color: colors.gray500,
                          }}
                        >
                          No time entries
                        </div>
                      ) : (
                        <>
                          {/* Stats Header */}
                          {(() => {
                            const totalHours = entries.reduce(
                              (sum, e) => sum + (e.total_hours || 0),
                              0,
                            );
                            const allocatedHours = entries.reduce(
                              (sum, e) => sum + getAllocatedHours(e.id),
                              0,
                            );
                            const remainingHours = totalHours - allocatedHours;

                            return (
                              <div
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                                  padding: 14,
                                  display: "flex",
                                  justifyContent: "space-around",
                                }}
                              >
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: "white",
                                  }}
                                >
                                  <div
                                    style={{ fontSize: 16, fontWeight: 700 }}
                                  >
                                    {entries.length}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      opacity: 0.7,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Crew
                                  </div>
                                </div>
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: "white",
                                  }}
                                >
                                  <div
                                    style={{ fontSize: 16, fontWeight: 700 }}
                                  >
                                    {totalHours.toFixed(1)}h
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      opacity: 0.7,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Total
                                  </div>
                                </div>
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: "white",
                                  }}
                                >
                                  <div
                                    style={{ fontSize: 16, fontWeight: 700 }}
                                  >
                                    {allocatedHours.toFixed(1)}h
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      opacity: 0.7,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Allocated
                                  </div>
                                </div>
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: "white",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 16,
                                      fontWeight: 700,
                                      color:
                                        remainingHours > 0.1
                                          ? "#fbbf24"
                                          : "#86efac",
                                    }}
                                  >
                                    {remainingHours.toFixed(1)}h
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 9,
                                      opacity: 0.7,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Remaining
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Voice Bar - Primary (hidden when locked) */}
                          {!isLocked && (
                            <div
                              style={{
                                background:
                                  "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                padding: "12px 14px",
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                              }}
                            >
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  background: "rgba(0,0,0,0.2)",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 20,
                                  flexShrink: 0,
                                }}
                              >
                                🎤
                              </div>
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    color: "rgba(0,0,0,0.8)",
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  Tap to speak time entries
                                </div>
                                <div
                                  style={{
                                    color: "rgba(0,0,0,0.6)",
                                    fontSize: 11,
                                    marginTop: 2,
                                    fontStyle: "italic",
                                  }}
                                >
                                  "Ryan 8 hours block, Johnny 7 to 3:30 grout"
                                </div>
                              </div>
                              <VoiceButton
                                contextType="universal"
                                jobId={report.job_id}
                                compact
                                screenContext={{
                                  crew_roster: entries.map((e) => {
                                    const name =
                                      e.employee?.display_name ||
                                      `${e.employee?.first_name || ""} ${e.employee?.last_name || ""}`.trim();
                                    return `${name} (${e.total_hours || 0}h)`;
                                  }),
                                  crew_hours: entries.map((e) => ({
                                    name:
                                      e.employee?.display_name ||
                                      `${e.employee?.first_name || ""} ${e.employee?.last_name || ""}`.trim(),
                                    hours: e.total_hours || 0,
                                  })),
                                  job_cost_codes: jobCostCodes.map(
                                    (cc) => `${cc.code} - ${cc.name}`,
                                  ),
                                  total_clocked: entries.reduce(
                                    (sum, e) => sum + (e.total_hours || 0),
                                    0,
                                  ),
                                }}
                                onResult={(result) =>
                                  handleVoiceResult(result, report)
                                }
                                onError={(err) => setVoiceError(err.message)}
                                onRecordingChange={(recording) => {
                                  if (recording) setVoiceProcessing(report.id);
                                }}
                                disabled={
                                  voiceProcessing &&
                                  voiceProcessing !== report.id
                                }
                                style={{
                                  background: "rgba(0,0,0,0.2)",
                                  color: "#000",
                                  border: "none",
                                  padding: "8px 16px",
                                  borderRadius: 20,
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              />
                            </div>
                          )}

                          {/* Locked banner */}
                          {isLocked && (
                            <div
                              style={{
                                background: colors.success,
                                padding: "10px 14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                              }}
                            >
                              <span style={{ fontSize: 14 }}>🔒</span>
                              <span
                                style={{
                                  color: "white",
                                  fontSize: 13,
                                  fontWeight: 600,
                                }}
                              >
                                Approved by Office - Time edits only
                              </span>
                            </div>
                          )}

                          {/* Voice error display */}
                          {voiceError && (
                            <div
                              style={{
                                padding: "10px 14px",
                                background: "#fef2f2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span
                                style={{ fontSize: 12, color: colors.danger }}
                              >
                                {voiceError}
                              </span>
                              <button
                                onClick={() => setVoiceError(null)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: colors.danger,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          )}

                          {/* Content */}
                          <div style={{ padding: 10 }}>
                            {/* Section Header */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#666",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                Crew ({entries.length})
                              </span>
                              {!isLocked && (
                                <button
                                  onClick={() => setBulkMode(!bulkMode)}
                                  style={{
                                    fontSize: 11,
                                    color: colors.primary,
                                    fontWeight: 500,
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  ☑️ {bulkMode ? "Exit Bulk" : "Bulk Mode"}
                                </button>
                              )}
                            </div>

                            {/* Crew Cards */}
                            {entries.map((entry) => {
                              const totalHours = entry.total_hours || 0;
                              const allocatedHours = getAllocatedHours(
                                entry.id,
                              );
                              const remaining = Math.max(
                                0,
                                totalHours - allocatedHours,
                              );
                              const isFullyAllocated =
                                api.isEntryFullyAllocated(
                                  totalHours,
                                  allocatedHours,
                                );
                              const isSelected = selectedEntries.has(entry.id);
                              const primaryAlloc = getPrimaryAllocation(
                                entry,
                                jobCostCodes,
                              );

                              return (
                                <div
                                  key={entry.id}
                                  style={{
                                    background: "#fff",
                                    border: isFullyAllocated
                                      ? "1.5px solid #86efac"
                                      : remaining > 0
                                        ? "1.5px solid #fcd34d"
                                        : "1.5px solid #e5e7eb",
                                    borderRadius: 10,
                                    padding: 10,
                                    marginBottom: 8,
                                    backgroundColor: isFullyAllocated
                                      ? "#f0fdf4"
                                      : remaining > 0
                                        ? "#fffbeb"
                                        : "#fff",
                                  }}
                                >
                                  {/* Crew Top Row */}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    {/* Bulk Checkbox */}
                                    {bulkMode && (
                                      <div
                                        onClick={() =>
                                          toggleEntrySelection(entry.id)
                                        }
                                        style={{
                                          width: 20,
                                          height: 20,
                                          border: isSelected
                                            ? `2px solid ${colors.primary}`
                                            : "2px solid #d1d5db",
                                          borderRadius: 5,
                                          background: isSelected
                                            ? colors.primary
                                            : "white",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: "pointer",
                                          flexShrink: 0,
                                        }}
                                      >
                                        {isSelected && (
                                          <span
                                            style={{
                                              color: "white",
                                              fontSize: 10,
                                              fontWeight: 700,
                                            }}
                                          >
                                            ✓
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Crew Info */}
                                    <div style={{ flex: 1 }}>
                                      <div
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          color: "#1f2937",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                        }}
                                      >
                                        {entry.employee?.display_name ||
                                          `${entry.employee?.first_name || ""} ${entry.employee?.last_name || ""}`.trim() ||
                                          "Unknown"}
                                        {isFullyAllocated ? (
                                          <span
                                            style={{
                                              padding: "2px 6px",
                                              borderRadius: 4,
                                              fontSize: 9,
                                              fontWeight: 600,
                                              background: "#dcfce7",
                                              color: "#166534",
                                            }}
                                          >
                                            ✓
                                          </span>
                                        ) : (
                                          <span
                                            style={{
                                              padding: "2px 6px",
                                              borderRadius: 4,
                                              fontSize: 9,
                                              fontWeight: 600,
                                              background: "#fef3c7",
                                              color: "#92400e",
                                            }}
                                          >
                                            {remaining.toFixed(1)}h left
                                          </span>
                                        )}
                                        <EditIndicator
                                          entry={entry}
                                          editHistoryMap={editHistoryMap}
                                          onLoadHistory={loadEditHistory}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: "#6b7280",
                                          marginTop: 1,
                                        }}
                                      >
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTimeEdit(entry);
                                          }}
                                          style={{
                                            color: colors.primary,
                                            textDecoration: "underline",
                                            textDecorationStyle: "dotted",
                                            cursor: "pointer",
                                          }}
                                        >
                                          {formatTime(entry.clock_in)}
                                        </span>
                                        {" → "}
                                        <span
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTimeEdit(entry);
                                          }}
                                          style={{
                                            color: colors.primary,
                                            textDecoration: "underline",
                                            textDecorationStyle: "dotted",
                                            cursor: "pointer",
                                          }}
                                        >
                                          {entry.clock_out
                                            ? formatTime(entry.clock_out)
                                            : "Working"}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Hours */}
                                    <div style={{ textAlign: "right" }}>
                                      <div
                                        style={{
                                          fontSize: 16,
                                          fontWeight: 700,
                                          color: "#1f2937",
                                        }}
                                      >
                                        {totalHours.toFixed(1)}h
                                      </div>
                                    </div>
                                  </div>

                                  {/* Cost Code Display Row (read-only when locked) */}
                                  <div
                                    onClick={(e) => {
                                      if (isLocked) return;
                                      e.stopPropagation();
                                      setPickingCostCode(entry);
                                    }}
                                    style={{
                                      marginTop: 8,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "6px 8px",
                                      background: primaryAlloc
                                        ? "#dcfce7"
                                        : "#fef3c7",
                                      borderRadius: 6,
                                      fontSize: 12,
                                      cursor: isLocked ? "default" : "pointer",
                                      opacity: isLocked ? 0.8 : 1,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: primaryAlloc
                                          ? "#166534"
                                          : "#d97706",
                                      }}
                                    >
                                      {primaryAlloc
                                        ? primaryAlloc.costCode?.code
                                        : "—"}
                                    </span>
                                    <span style={{ flex: 1, color: "#374151" }}>
                                      {primaryAlloc
                                        ? primaryAlloc.costCode?.name
                                        : isLocked
                                          ? "Not allocated"
                                          : "Tap or say to assign"}
                                    </span>
                                    {primaryAlloc && (
                                      <span style={{ fontWeight: 600 }}>
                                        {primaryAlloc.hours}h
                                      </span>
                                    )}
                                    {!isLocked && (
                                      <span style={{ color: "#9ca3af" }}>
                                        ▼
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            {/* Add Crew Button (hidden when locked) */}
                            {!isLocked && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowAddCrew(report.id);
                                  if (allEmployees.length === 0)
                                    loadAllEmployees();
                                }}
                                style={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 8,
                                  padding: "10px 16px",
                                  marginTop: 8,
                                  background: "transparent",
                                  border: "1px dashed #d1d5db",
                                  borderRadius: 10,
                                  color: "#6b7280",
                                  fontSize: 13,
                                  fontWeight: 500,
                                  cursor: "pointer",
                                }}
                              >
                                <span style={{ fontSize: 16 }}>+</span>
                                Add Missing Crew
                              </button>
                            )}

                            {/* Bulk Allocate Panel */}
                            {bulkMode && selectedEntries.size > 0 && (
                              <div
                                style={{
                                  background: "#f0f7ff",
                                  border: `2px solid ${colors.primary}`,
                                  borderRadius: 10,
                                  padding: 12,
                                  marginTop: 12,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: "#1e3a5f",
                                    }}
                                  >
                                    Bulk Allocate
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: colors.primary,
                                    }}
                                  >
                                    {selectedEntries.size} selected
                                  </div>
                                </div>

                                {/* Cost Code Selection */}
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 6,
                                    marginBottom: 10,
                                  }}
                                >
                                  {jobCostCodes.map((cc) => (
                                    <button
                                      key={cc.id}
                                      onClick={() => setSelectedCostCode(cc)}
                                      style={{
                                        padding: "6px 10px",
                                        borderRadius: 6,
                                        border:
                                          selectedCostCode?.id === cc.id
                                            ? `2px solid ${colors.primary}`
                                            : "1px solid #d1d5db",
                                        background:
                                          selectedCostCode?.id === cc.id
                                            ? colors.primary
                                            : "white",
                                        color:
                                          selectedCostCode?.id === cc.id
                                            ? "white"
                                            : "#374151",
                                        fontSize: 11,
                                        fontWeight: 600,
                                        cursor: "pointer",
                                      }}
                                    >
                                      {cc.code}
                                    </button>
                                  ))}
                                </div>

                                {/* Hours + Apply */}
                                <div style={{ display: "flex", gap: 8 }}>
                                  <input
                                    type="number"
                                    step="0.25"
                                    placeholder="Hours"
                                    value={quickHours}
                                    onChange={(e) =>
                                      setQuickHours(e.target.value)
                                    }
                                    style={{
                                      flex: 1,
                                      padding: "8px 10px",
                                      borderRadius: 6,
                                      border: "1px solid #d1d5db",
                                      fontSize: 12,
                                    }}
                                  />
                                  <button
                                    onClick={() => applyAllocation(report)}
                                    disabled={
                                      !selectedCostCode ||
                                      !quickHours ||
                                      savingAllocations
                                    }
                                    style={{
                                      padding: "10px 16px",
                                      borderRadius: 6,
                                      border: "none",
                                      background:
                                        selectedCostCode && quickHours
                                          ? colors.primary
                                          : "#e5e7eb",
                                      color:
                                        selectedCostCode && quickHours
                                          ? "white"
                                          : "#9ca3af",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor:
                                        selectedCostCode && quickHours
                                          ? "pointer"
                                          : "not-allowed",
                                    }}
                                  >
                                    {savingAllocations ? "..." : "Apply"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Bottom Actions (hidden when locked) */}
                          {!isLocked && (
                            <div
                              style={{
                                display: "flex",
                                gap: 10,
                                marginTop: 16,
                                paddingTop: 16,
                                borderTop: `1px solid ${colors.gray200}`,
                              }}
                            >
                              {(report.status === "draft" ||
                                report.status === "needs_correction") && (
                                <button
                                  onClick={() => handleSubmitReport(report)}
                                  disabled={approvingReport === report.id}
                                  style={{
                                    flex: 1,
                                    padding: 14,
                                    borderRadius: 12,
                                    border: "none",
                                    background: colors.info,
                                    color: colors.white,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    cursor:
                                      approvingReport === report.id
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      approvingReport === report.id ? 0.7 : 1,
                                  }}
                                >
                                  {approvingReport === report.id
                                    ? "Submitting..."
                                    : "Submit for Approval"}
                                </button>
                              )}

                              {report.status === "submitted" &&
                                ["admin", "office"].includes(
                                  currentUser?.employee_type,
                                ) && (
                                <button
                                  onClick={() => handleApproveReport(report)}
                                  disabled={approvingReport === report.id}
                                  style={{
                                    flex: 1,
                                    padding: 14,
                                    borderRadius: 12,
                                    border: "none",
                                    background: colors.success,
                                    color: colors.white,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    cursor:
                                      approvingReport === report.id
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      approvingReport === report.id ? 0.7 : 1,
                                  }}
                                >
                                  {approvingReport === report.id
                                    ? "Approving..."
                                    : "Approve Timesheet"}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Crew Modal */}
      {showAddCrew && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={() => setShowAddCrew(null)}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              padding: "24px",
              paddingBottom: "40px",
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "4px",
                background: colors.gray300,
                borderRadius: "2px",
                margin: "0 auto 16px",
              }}
            />

            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: colors.gray800,
                }}
              >
                Add Crew Member
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: colors.gray500,
                  marginTop: "4px",
                }}
              >
                Select employee to add to this timesheet
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {loadingEmployees ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: colors.gray500,
                  }}
                >
                  Loading...
                </div>
              ) : (
                (() => {
                  const report = reports.find((r) => r.id === showAddCrew);
                  const availableEmployees = report
                    ? getAvailableEmployees(report)
                    : [];

                  if (availableEmployees.length === 0) {
                    return (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: colors.gray500,
                        }}
                      >
                        No available employees to add
                      </div>
                    );
                  }

                  return availableEmployees.map((emp) => (
                    <button
                      key={emp.id}
                      onClick={() => handleAddCrewMember(emp, report)}
                      disabled={addingEmployee === emp.id}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background:
                          addingEmployee === emp.id
                            ? colors.gray100
                            : "transparent",
                        border: "none",
                        borderBottom: `1px solid ${colors.gray100}`,
                        cursor: addingEmployee ? "wait" : "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            background: emp.avatar_color || colors.gray500,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                        >
                          {(
                            (emp.first_name?.[0] || "") +
                            (emp.last_name?.[0] || "")
                          ).toUpperCase()}
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "15px",
                              fontWeight: "500",
                              color: colors.gray700,
                            }}
                          >
                            {emp.display_name ||
                              `${emp.first_name} ${emp.last_name}`}
                          </div>
                          <div
                            style={{ fontSize: "13px", color: colors.gray400 }}
                          >
                            {emp.role}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "6px 12px",
                          borderRadius: "8px",
                          background: colors.success,
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {addingEmployee === emp.id ? "Adding..." : "Add"}
                      </div>
                    </button>
                  ));
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {/* Time Edit Modal */}
      {editingEntry && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={() => setEditingEntry(null)}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              padding: "24px",
              paddingBottom: "40px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "4px",
                background: colors.gray300,
                borderRadius: "2px",
                margin: "0 auto 16px",
              }}
            />

            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: colors.gray800,
                }}
              >
                Edit Time
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: colors.gray500,
                  marginTop: "4px",
                }}
              >
                {editingEntry.employee?.display_name ||
                  `${editingEntry.employee?.first_name || ""} ${editingEntry.employee?.last_name || ""}`.trim()}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.gray600,
                    marginBottom: 6,
                  }}
                >
                  Clock In
                </label>
                <input
                  type="time"
                  value={editClockIn}
                  onChange={(e) => setEditClockIn(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${colors.gray300}`,
                    fontSize: 16,
                    color: colors.gray800,
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.gray600,
                    marginBottom: 6,
                  }}
                >
                  Clock Out
                </label>
                <input
                  type="time"
                  value={editClockOut}
                  onChange={(e) => setEditClockOut(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1px solid ${colors.gray300}`,
                    fontSize: 16,
                    color: colors.gray800,
                  }}
                />
              </div>

              {/* Calculated Hours Display */}
              {editClockIn && editClockOut && (
                <div
                  style={{
                    padding: "12px 14px",
                    background: colors.gray100,
                    borderRadius: 10,
                    textAlign: "center",
                  }}
                >
                  <span style={{ fontSize: 12, color: colors.gray500 }}>
                    Total Hours:{" "}
                  </span>
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: colors.gray800,
                    }}
                  >
                    {(() => {
                      const [inH, inM] = editClockIn.split(":").map(Number);
                      const [outH, outM] = editClockOut.split(":").map(Number);
                      const inMins = inH * 60 + inM;
                      const outMins = outH * 60 + outM;
                      const diff = outMins - inMins;
                      if (diff <= 0) return "0.0h";
                      return (diff / 60).toFixed(1) + "h";
                    })()}
                  </span>
                </div>
              )}

              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button
                  onClick={() => setEditingEntry(null)}
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: 12,
                    border: `1px solid ${colors.gray300}`,
                    background: "white",
                    color: colors.gray600,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const report = reports.find(
                      (r) => r.id === expandedReportId,
                    );
                    if (report) saveTimeEdit(report);
                  }}
                  style={{
                    flex: 1,
                    padding: "14px",
                    borderRadius: 12,
                    border: "none",
                    background: colors.primary,
                    color: "white",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Save Time
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Code Picker Modal */}
      {pickingCostCode && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={() => setPickingCostCode(null)}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              padding: "24px",
              paddingBottom: "40px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "4px",
                background: colors.gray300,
                borderRadius: "2px",
                margin: "0 auto 16px",
              }}
            />

            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: colors.gray800,
                }}
              >
                Assign Cost Code
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: colors.gray500,
                  marginTop: "4px",
                }}
              >
                {pickingCostCode.employee?.display_name ||
                  `${pickingCostCode.employee?.first_name || ""} ${pickingCostCode.employee?.last_name || ""}`.trim()}{" "}
                - {(pickingCostCode.total_hours || 0).toFixed(1)}h
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {(() => {
                const report = reports.find((r) => r.id === expandedReportId);
                const jobCostCodes = report
                  ? costCodesMap[report.job_id] || []
                  : [];
                const totalHours = pickingCostCode.total_hours || 0;
                const currentAllocs = allocationsMap[pickingCostCode.id] || {};
                const allocatedHours = Object.values(currentAllocs).reduce(
                  (sum, h) => sum + (parseFloat(h) || 0),
                  0,
                );
                const remainingHours = Math.max(0, totalHours - allocatedHours);

                return (
                  <>
                    {/* Current Allocations */}
                    {Object.keys(currentAllocs).length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: colors.gray500,
                            textTransform: "uppercase",
                            marginBottom: 8,
                          }}
                        >
                          Current Allocations
                        </div>
                        {Object.entries(currentAllocs).map(([ccId, hours]) => {
                          const cc = jobCostCodes.find((c) => c.id === ccId);
                          if (!cc || !hours) return null;
                          return (
                            <div
                              key={ccId}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "10px 12px",
                                background: "#dcfce7",
                                borderRadius: 8,
                                marginBottom: 6,
                              }}
                            >
                              <div>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: "#166534",
                                    marginRight: 8,
                                  }}
                                >
                                  {cc.code}
                                </span>
                                <span style={{ color: colors.gray600 }}>
                                  {cc.name}
                                </span>
                              </div>
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: "#166534",
                                }}
                              >
                                {hours}h
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Remaining Hours Indicator */}
                    <div
                      style={{
                        padding: "10px 14px",
                        background:
                          remainingHours > 0.1 ? "#fef3c7" : "#dcfce7",
                        borderRadius: 10,
                        marginBottom: 16,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 13, color: colors.gray600 }}>
                        Remaining to allocate
                      </span>
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: remainingHours > 0.1 ? "#d97706" : "#166534",
                        }}
                      >
                        {remainingHours.toFixed(1)}h
                      </span>
                    </div>

                    {/* Cost Code List */}
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.gray500,
                        textTransform: "uppercase",
                        marginBottom: 8,
                      }}
                    >
                      Available Cost Codes
                    </div>
                    {jobCostCodes.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: colors.gray500,
                        }}
                      >
                        No cost codes available
                      </div>
                    ) : (
                      jobCostCodes.map((cc) => (
                        <button
                          key={cc.id}
                          onClick={() => {
                            // Quick-assign all remaining hours to this cost code
                            if (remainingHours > 0.1 && report) {
                              assignCostCode(
                                pickingCostCode,
                                cc,
                                parseFloat(remainingHours.toFixed(2)),
                                report,
                              );
                            } else {
                              // Replace with full hours
                              assignCostCode(
                                pickingCostCode,
                                cc,
                                totalHours,
                                report,
                              );
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "14px 16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: "white",
                            border: `1px solid ${colors.gray200}`,
                            borderRadius: 10,
                            marginBottom: 8,
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <div>
                            <span
                              style={{
                                fontWeight: 700,
                                color: colors.primary,
                                marginRight: 10,
                                fontSize: 14,
                              }}
                            >
                              {cc.code}
                            </span>
                            <span
                              style={{ color: colors.gray700, fontSize: 14 }}
                            >
                              {cc.name}
                            </span>
                          </div>
                          <div
                            style={{
                              padding: "6px 12px",
                              borderRadius: 8,
                              background: colors.primary,
                              color: "white",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {remainingHours > 0.1
                              ? `+${remainingHours.toFixed(1)}h`
                              : `${totalHours.toFixed(1)}h`}
                          </div>
                        </button>
                      ))
                    )}
                  </>
                );
              })()}
            </div>

            <button
              onClick={() => setPickingCostCode(null)}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: 12,
                border: `1px solid ${colors.gray300}`,
                background: "white",
                color: colors.gray600,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                marginTop: 16,
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
